// @LTG-ID: data/journals/business-value-drivers
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 11 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "business-value-drivers",
    FRONTMATTER: {
        number: "48",
        section: "Value Drivers",
        sectionHref: "/journals#value-drivers",
        sectionAnchor: "value-drivers",
        title: "The Value Drivers That Determine What Buyers Will Pay for Your Business",
        deck: "Business value drivers are the characteristics that determine where a company falls within its industry's valuation range.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-03-26",
        publishedHuman: "March 26, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 4,
        wordCount: 683,
        timeRequired: "PT4M",
        keywords: "business value drivers, what increases business value, value drivers when selling a business, drivers of company value",
        articleSection: "Value Drivers",
        articleBodyPreview: "Business value drivers are the characteristics that determine where a company falls within its industry's valuation range. The most important are earnings quality, recurring revenue, customer diversity, independence from the owner, management depth, documented systems, reliable financial records, and growth trend. Improving them raises both price and deal terms. Two businesses in the same industry with the same profit routinely sell for very different prices. The difference is never magic. It is",
        complianceNote: "This article is educational and does not constitute a valuation engagement or financial, legal, or tax advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-much-is-my-business-worth",
        "owner-dependence-business-value",
        "recurring-revenue-business-value",
        "customer-concentration-selling-business",
        "clean-financial-records-business-value",
        "management-team-business-value",
        "business-systems-processes-value",
        "does-growth-increase-business-value",
        "increase-business-value-before-selling"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Value Drivers: What Buyers Pay More for Your Business · LTG",
        canonical: "https://letstalkgrowth.com/journals/business-value-drivers",
        ogImage: "/journals/og/business-value-drivers.jpg",
    },
    BODY_MARKDOWN: `Business value drivers are the characteristics that determine where a company falls within its industry's valuation range. The most important are earnings quality, recurring revenue, customer diversity, independence from the owner, management depth, documented systems, reliable financial records, and growth trend. Improving them raises both price and deal terms.

Two businesses in the same industry with the same profit routinely sell for very different prices. The difference is never magic. It is a set of observable characteristics that buyers, lenders, and appraisers all evaluate, informally or formally, on every deal. Owners who understand these drivers stop asking "what is the multiple for my industry" and start asking the better question: "what would move my business toward the top of its range?"

## Driver 1: Earnings quality

Everything begins with earnings a buyer can believe. That means profits that are documented, consistent, and honestly normalized. A dollar of earnings supported by accountant-prepared statements and clean tax returns is worth more than a dollar that has to be taken on faith. Aggressive add-backs, commingled personal expenses, and unexplained swings all convert directly into discounts, because buyers price uncertainty as risk.

## Driver 2: Recurring and contracted revenue

Buyers pay for the future, and revenue that repeats by design, service agreements, maintenance contracts, memberships, retainers, is a future they can see. Project-based revenue that must be re-won every month restarts at zero for a new owner. Across nearly every sector, from HVAC service plans to dental recall programs to manufacturing supply agreements, shifting even part of the revenue base to recurring forms strengthens both price and buyer confidence. We cover the mechanics in the recurring revenue article in this series.

## Driver 3: Customer diversity

Concentration is the sharpest-edged driver: it can not only lower a price but end a deal. When a large share of revenue depends on one or two customers, the buyer inherits a risk they cannot control. Lenders underwriting acquisition loans scrutinize concentration as closely as buyers do. The companion article on customer concentration covers the commonly used thresholds and what to do if you are past them.

## Driver 4: Independence from the owner

If the business's sales, relationships, and decisions all route through the owner, the buyer is purchasing something that may evaporate at closing. Owner dependence is so consequential that we gave it a full article of its own. The short version: document what you know, delegate real authority, transfer relationships, and build the track record that proves the business runs without you.

## Driver 5: Management and team depth

A capable second layer, people who can sell, operate, and manage without daily direction, tells a buyer the business is a system rather than a personality. Team depth also expands the buyer pool: financial buyers and out-of-industry buyers can only purchase businesses that come with management.

## Driver 6: Documented systems and processes

Documentation converts tribal knowledge into transferable assets. Price books, onboarding checklists, standard operating procedures, and maintained customer records all reduce the buyer's transition risk. None of this is glamorous, and that is exactly why it differentiates: most sellers never do it.

## Driver 7: Financial record quality

Beyond believable earnings, buyers and their lenders need records that survive diligence: reconciled books, consistent accounting methods, defensible inventory counts, and tax returns that match the statements. Record quality does not just affect price; it affects speed, and slow deals die.

## Driver 8: Growth trend and market position

A three-year upward trend supports a story buyers pay for; decline forces explanations. Alongside trend, buyers evaluate the durability of your position: reputation, reviews, contracts, location, licenses, and whatever else makes customers choose you repeatedly in a competitive market.

## How the drivers work together

These drivers compound. Clean records make earnings believable; believable earnings plus recurring revenue attract lenders; financeable deals attract more buyers; more buyers create competition; competition raises price and improves terms. The practical program for any owner is to assess each driver honestly, pick the two or three weakest, and spend the years before a sale fixing them. That, in one paragraph, is what value building means.`,
    FAQ: [
        { question: "Which value driver matters most?", answer: "It varies by business, but owner independence and earnings quality are the most universal. A business that runs without its owner and proves its profits with clean records clears the two hurdles that kill the most deals." },
        { question: "How long does it take to improve value drivers?", answer: "Most meaningful improvements take one to three years to implement and then show up in the trailing results buyers evaluate. This is the core argument for starting exit preparation years before a planned sale." },
        { question: "Do value drivers matter if I am not selling?", answer: "Yes. Every driver on the list, from recurring revenue to team depth, also makes a business more profitable, more resilient, and easier to own. Value building and good management are largely the same work." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Value Drivers",
            title: "Owner Dependence: The Biggest Hidden Discount on Your Business's Value",
            meta: "3 min · Brief 45",
            to: "/journals/owner-dependence-business-value",
        },
        {
            tag: "Value Drivers",
            title: "Recurring Revenue: Why Buyers Pay More for Businesses That Bill Every Month",
            meta: "3 min · Brief 49",
            to: "/journals/recurring-revenue-business-value",
        },
    ],
};

export default BRIEF;
